// src/components/HeaderBar.jsx
"use client";

import Link from "next/link";
import NextImage from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import AvatarMenu from "@/components/AvatarMenu";
import { Search as SearchIcon } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import PortalPrimaryNavigation from "@/components/portal/PortalPrimaryNavigation";
import { useTypeaheadSearch } from "@/hooks/useTypeaheadSearch";
import { usePortal } from "@/context/PortalContext";

export default function HeaderBar() {
  const portal = usePortal();
  const { loading } = useAuth();
  const router = useRouter();
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimerRef = useRef(null);

  const effectiveQuery = useMemo(
    () => (query || "").trim().replace(/^[@+#]/, ""),
    [query]
  );

  const typeahead = useTypeaheadSearch({
    query: effectiveQuery,
    types: portal ? { users: false, agents: false, hubs: true } : { users: true, agents: true, hubs: true },
    allowedHubSlugs: portal?.allowedHubSlugs || null,
    limit: 5,
    debounceMs: 150,
  });

  const suggestionSections = useMemo(() => {
    const sections = [];
    const users = (typeahead.results.users || []).slice(0, 5).map((u) => ({
      key: `user-${u.id}`,
      label: u.displayName || `@${u.handle}`,
      handle: u.handle,
      avatar: u.avatar || "",
      type: "user",
    }));
    const agents = (typeahead.results.agents || []).slice(0, 5).map((a) => ({
      key: `agent-${a.id}`,
      label: a.displayName || `@${a.handle}`,
      handle: a.handle,
      avatar: a.avatar || "",
      type: "agent",
    }));
    const hubs = (typeahead.results.hubs || []).slice(0, 5).map((h) => ({
      key: `hub-${h.id}`,
      label: h.displayName || h.slug,
      handle: h.slug,
      avatar: h.badgeUrl || "",
      type: "hub",
    }));
    if (users.length) sections.push({ title: "People", items: users });
    if (agents.length) sections.push({ title: "Agents", items: agents });
    if (hubs.length) sections.push({ title: "Spaces", items: hubs });
    return sections;
  }, [typeahead.results.users, typeahead.results.agents, typeahead.results.hubs]);

  const suggestionsFlat = useMemo(
    () => suggestionSections.flatMap((s) => s.items),
    [suggestionSections]
  );

  useEffect(() => {
    if (highlight >= suggestionsFlat.length) setHighlight(0);
  }, [suggestionsFlat.length, highlight]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const handleSelect = (item) => {
    if (!item) return;
    const path =
      item.type === "hub"
        ? `/h/${encodeURIComponent(item.handle)}`
        : item.type === "agent"
        ? `/agent/${encodeURIComponent(item.handle)}`
        : `/u/${encodeURIComponent(item.handle)}`;
    router.push(path);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (!open && e.key === "Enter") {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!suggestionsFlat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((idx) => (idx + 1) % suggestionsFlat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((idx) => (idx - 1 + suggestionsFlat.length) % suggestionsFlat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(suggestionsFlat[highlight] || suggestionsFlat[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setMobileOpen(false);
    }
  };

  const closeAfterBlur = () => {
    blurTimerRef.current = setTimeout(() => setOpen(false), 100);
  };

  const handleFocus = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setOpen(true);
  };

  useEffect(() => {
    if (mobileOpen) {
      setOpen(true);
      requestAnimationFrame(() => {
        mobileInputRef.current?.focus();
      });
    } else {
      setOpen(false);
      setQuery("");
    }
  }, [mobileOpen]);

  return (
    <header className="site-header sticky top-0 z-[70] bar-translucent">
      <div className={`site-header-inner flex items-center gap-3 ${portal ? "portal-header-inner" : "container-app"}`}>
        {/* Logo */}
        {portal ? (
          <div className="flex min-w-0 items-center gap-2.5">
            {portal.branding.publisherLogoLinkMode === "none" ? (
              <PortalPublisherBrand portal={portal} />
            ) : (
              <a
                href={portal.branding.publisherWebsiteUrl}
                target={portal.branding.publisherLogoLinkMode === "new-tab" ? "_blank" : undefined}
                rel={portal.branding.publisherLogoLinkMode === "new-tab" ? "noopener noreferrer" : undefined}
                className="flex min-w-0 items-center gap-2.5 text-white"
                aria-label={`Visit ${portal.name}`}
              >
                <PortalPublisherBrand portal={portal} />
              </a>
            )}
          </div>
        ) : (
          <Link href="/" className="flex items-center" aria-label="Sproutly home">
            <NextImage
              src="/brand/sproutly-horizontal-logo.svg"
              alt="Sproutly"
              width={152}
              height={37}
              priority
              className="w-auto"
              style={{ height: "1.875rem" }}
            />
          </Link>
        )}

        {/* Search */}
        <div className="ml-4 hidden flex-1 md:flex">
          <div className="relative w-full max-w-md" onKeyDown={handleKeyDown}>
            <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-secondary)] opacity-80" aria-hidden />
            <input
              type="search"
              placeholder={portal ? "Search portal spaces" : "Search posts, spaces, people"}
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleFocus}
              onBlur={closeAfterBlur}
              className="brand-focus w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] pl-9 pr-16 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-secondary)]"
              aria-label="Search"
            />
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 kbd">Ctrl K</kbd>

            {open && (typeahead.loading || suggestionsFlat.length > 0 || query.length >= 2) ? (
              <div
                className="absolute left-0 right-0 top-full z-[80] mt-1 max-h-72 overflow-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]"
                role="listbox"
              >
                {typeahead.loading ? (
                  <div className="px-3 py-2 text-sm text-[var(--muted)]">Searching…</div>
                ) : null}
                {suggestionSections.map((section) => (
                  <div key={section.title}>
                    <div className="px-3 pt-2 text-[11px] uppercase tracking-wide text-[var(--muted)]">
                      {section.title}
                    </div>
                    {section.items.map((item) => {
                      const flatIndex = suggestionsFlat.findIndex((it) => it.key === item.key);
                      const active = flatIndex === highlight;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={[
                            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface-2)]",
                            active ? "bg-[var(--surface-2)]" : "",
                          ].join(" ")}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelect(item)}
                        >
                          {item.avatar ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={item.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-[var(--muted)]/20" aria-hidden />
                          )}
                          <div className="flex-1 truncate">
                            <div className="truncate font-medium">
                              {item.type === "hub" ? `+${item.handle}` : `@${item.handle}`}
                            </div>
                            <div className="truncate text-xs text-[var(--muted)]">
                              {item.label}
                              {item.type === "agent" ? " • Agent" : null}
                              {item.type === "hub" ? " • Space" : null}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
                {!typeahead.loading && suggestionsFlat.length === 0 && query.length >= 2 ? (
                  <div className="px-3 py-2 text-sm text-[var(--muted)]">No matches</div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            className="grid h-11 w-11 place-items-center rounded-full text-[var(--fg)] hover:bg-[var(--surface-2)] md:hidden"
            aria-label="Search"
            onClick={() => setMobileOpen(true)}
          >
            <SearchIcon size={18} />
          </button>
          <NotificationBell />
          {!loading && <AvatarMenu />}
        </div>
      </div>

      {portal ? <PortalPrimaryNavigation /> : null}

      {/* Mobile search overlay */}
      {mobileOpen ? (
        <div
          className="mobile-search-overlay fixed inset-0 z-[90] bg-[var(--bg)]/95 px-4 md:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close search"
              className="grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--card)] text-[var(--fg)] hover:bg-[var(--surface-2)]"
              onClick={() => setMobileOpen(false)}
            >
              ×
            </button>
            <div className="relative flex-1">
              <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-secondary)] opacity-80" aria-hidden />
              <input
                ref={mobileInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search people, agents, spaces"
                className="brand-focus w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] pl-9 pr-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-secondary)]"
                aria-label="Search"
              />
            </div>
          </div>
          <div
            className="mt-2 max-h-[70vh] overflow-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]"
            onClick={(e) => e.stopPropagation()}
          >
            {typeahead.loading ? (
              <div className="px-3 py-2 text-sm text-[var(--muted)]">Searching…</div>
            ) : null}
            {suggestionSections.map((section) => (
              <div key={section.title}>
                <div className="px-3 pt-2 text-[11px] uppercase tracking-wide text-[var(--muted)]">
                  {section.title}
                </div>
                {section.items.map((item) => {
                  const flatIndex = suggestionsFlat.findIndex((it) => it.key === item.key);
                  const active = flatIndex === highlight;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={[
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface-2)]",
                        active ? "bg-[var(--surface-2)]" : "",
                      ].join(" ")}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(item)}
                    >
                      {item.avatar ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={item.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-[var(--muted)]/20" aria-hidden />
                      )}
                      <div className="flex-1 truncate">
                        <div className="truncate font-medium">
                          {item.type === "hub" ? `+${item.handle}` : `@${item.handle}`}
                        </div>
                        <div className="truncate text-xs text-[var(--muted)]">
                          {item.label}
                          {item.type === "agent" ? " • Agent" : null}
                          {item.type === "hub" ? " • Space" : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
            {!typeahead.loading && suggestionsFlat.length === 0 && query.length >= 2 ? (
              <div className="px-3 py-2 text-sm text-[var(--muted)]">No matches</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </header>
  );
}

function PortalPublisherBrand({ portal }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5 text-white">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={portal.branding.logoUrl}
        alt=""
        className="h-9 w-9 shrink-0 rounded-md border border-white/20 object-cover"
      />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-base font-semibold sm:text-lg">{portal.name}</span>
        <span className="whitespace-nowrap text-[9px] font-medium uppercase text-white/55">
          Powered by Sproutly
        </span>
      </span>
    </span>
  );
}
